import { AlertTriangle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface DeleteConfirmationModalProps {
  /**
   * Controls the open/close state of the modal
   */
  open: boolean;
  /**
   * Callback when the open state changes
   */
  onOpenChange: (open: boolean) => void;
  /**
   * Function to call when the delete action is confirmed
   */
  onConfirm: () => void;
  /**
   * Title of the modal
   * @default "Delete Confirmation"
   */
  title?: string;
  /**
   * Description text to show in the modal
   */
  description: React.ReactNode;
  /**
   * Text for the cancel button
   * @default "Cancel"
   */
  cancelText?: string;
  /**
   * Text for the confirm button
   * @default "Delete"
   */
  confirmText?: string;
  /**
   * Whether the action is in progress
   */
  isLoading?: boolean;
  /**
   * Show warning icon
   * @default true
   */
  showIcon?: boolean;
  /**
   * Additional class name for the confirm button
   */
  confirmButtonClass?: string;
}

export function DeleteConfirmationModal({
  open,
  onOpenChange,
  onConfirm,
  title = 'Delete Confirmation',
  description,
  cancelText = 'Cancel',
  confirmText = 'Delete',
  isLoading = false,
  showIcon = true,
  confirmButtonClass = '',
}: DeleteConfirmationModalProps) {
  const handleConfirm = (e: React.MouseEvent) => {
    e.preventDefault();
    onConfirm();
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            {showIcon && <AlertTriangle className="h-5 w-5 text-destructive" />}
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-2">
            {typeof description === 'string' ? (
              <p>{description}</p>
            ) : (
              description
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>
            {cancelText}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isLoading}
            className={`bg-destructive hover:bg-destructive/90 ${confirmButtonClass}`}
          >
            {isLoading ? 'Deleting...' : confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
